import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';

import { apiClient } from '@/services/legacy-api';
import { AuthContextType, User } from '@/types';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // With HttpOnly cookies, we can't check for a token in JS
        // Instead, try to fetch the current user - the cookie will be sent automatically
        const userData = await apiClient.getCurrentUser();
        const user = userData[0] ?? userData;

        const userInfo = {
          id: user.id ?? user._id,
          email: user.email,
          firstName: user.first_name ?? user.email,
          lastName: user.last_name,
          isClient: user.is_client,
          isNetworkPartner: user.is_network_partner,
          isAdministrator: user.is_administrator,
          teamId: user.team_id ?? user.team?.id,
          clientRole: user.client_role ?? null,
        };

        setUser(userInfo);

        // Update monitoring services with user info
        LogRocket.identify(userInfo.id, {
          name: userInfo.lastName
            ? `${userInfo.firstName} ${userInfo.lastName}`
            : userInfo.firstName,
          email: userInfo.email,
        });

        Sentry.setUser({
          id: userInfo.id,
          email: userInfo.email,
          username: `${userInfo.firstName} ${userInfo.lastName}`,
        });
      } catch (error) {
        // User is not authenticated or cookie is invalid
        // This is expected if the user is not logged in
        setUser(null);
        // Clear any leftover tokens from localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiry');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('tokenExpiry');
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.login(email, password);

        if (response.token) {
          // Store token based on rememberMe preference
          if (rememberMe) {
            localStorage.setItem('authToken', response.token);
            localStorage.setItem('rememberMe', 'true');
          } else {
            sessionStorage.setItem('authToken', response.token);
            localStorage.removeItem('rememberMe');
          }

          try {
            const userData = await apiClient.getCurrentUser();
            const user = userData[0];

            setUser({
              id: user.id ?? user._id,
              email: user.email,
              firstName: user.first_name ?? user.email,
              lastName: user.last_name,
              isClient: user.is_client,
              isNetworkPartner: user.is_network_partner,
              isAdministrator: user.is_administrator,
              teamId: user.team_id ?? user.team?.id,
              clientRole: user.client_role ?? null,
            });

            LogRocket.identify(user.id, {
              email: user.email,
              firstName: user.first_name,
              lastName: user.last_name,
              isClient: user.is_client,
              isNetworkPartner: user.is_network_partner,
              isAdministrator: user.is_administrator,
            });
            Sentry.setUser({
              id: user.id,
              email: user.email,
              username: `${user.first_name} ${user.last_name}`,
            });
          } catch (error) {
            // Token is invalid, clear tokens from both storages
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
            localStorage.removeItem('rememberMe');
          }

          // Navigate to home page
          navigate('/');
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.error ?? err.message ?? 'Login failed';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    await apiClient.logout();
    // Clear tokens from both storages
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('rememberMe');
    setUser(null);
    Sentry.setUser(null);
    navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** The auth context where there may be none: for a component that reads
 * the signed-in user to label what is theirs, but is also rendered
 * without a provider (a panel's own tests, a story). Null then. */
export function useAuthOptional(): AuthContextType | null {
  return useContext(AuthContext) ?? null;
}
